import { CdkVirtualScrollViewport, VirtualScrollStrategy } from '@angular/cdk/scrolling';
import { inject, Injectable, NgZone } from '@angular/core';
import { distinctUntilChanged, Observable, Subject } from 'rxjs';

/**
 * Custom VirtualScrollStrategy for mat-table inside cdk-virtual-scroll-viewport.
 *
 * The built-in FixedSizeVirtualScrollStrategy calculates the spacer as
 * `totalItems × itemSize`, but doesn't account for table header (thead),
 * footer (tfoot), or borders. This causes scrollHeight oscillation when
 * the content wrapper extends beyond the spacer.
 *
 * This strategy fixes the issue by:
 * - Including headerHeight in totalContentSize (spacer)
 * - Subtracting headerHeight from scroll offset for range calculation
 * - Properly calculating rendered range and content offset
 */
@Injectable()
export class TableVirtualScrollStrategy implements VirtualScrollStrategy {
  private _rowHeight = 48;
  private _headerHeight = 0;
  private _footerHeight = 0;
  private _bufferSize = 10;
  private _dataLength = 0;
  private _virtualScrollEnabled = true;

  private _viewport: CdkVirtualScrollViewport = null;

  private readonly _indexChange = new Subject<number>();
  public readonly scrolledIndexChange: Observable<number> = this._indexChange.pipe(distinctUntilChanged());
  private ngZone = inject(NgZone);

  attach(viewport: CdkVirtualScrollViewport): void {
    this._viewport = viewport;
    this.onDataLengthChanged();
  }

  detach(): void {
    this._viewport = null;
  }

  onContentScrolled(): void {
    this._updateContent();
  }

  onDataLengthChanged(): void {
    if (this._viewport) {
      const totalSize = this._dataLength * this._rowHeight + this._headerHeight + this._footerHeight;
      this._viewport.setTotalContentSize(totalSize);
      this._updateContent();
    }
  }

  onContentRendered(): void {}
  onRenderedOffsetChanged(): void {}

  scrollToIndex(index: number, behavior: ScrollBehavior): void {
    if (this._viewport) {
      this._viewport.scrollToOffset(index * this._rowHeight + this._headerHeight, behavior);
    }
  }

  setDataLength(length: number): void {
    this._dataLength = length;
    this.onDataLengthChanged();
  }

  setVirtualScrollEnabled(enabled: boolean): void {
    if (this._virtualScrollEnabled !== enabled) {
      this._virtualScrollEnabled = enabled;
      this.onDataLengthChanged();
    }
  }

  setScrollParameters(rowHeight: number, headerHeight: number, footerHeight: number, bufferSize: number): void {
    this._rowHeight = rowHeight;
    this._headerHeight = headerHeight;
    this._footerHeight = footerHeight;
    this._bufferSize = bufferSize;
    this.onDataLengthChanged();
  }

  updateHeaderHeight(height: number): void {
    if (height !== this._headerHeight) {
      this._headerHeight = height;
      this.onDataLengthChanged();
    }
  }

  updateFooterHeight(height: number): void {
    if (height !== this._footerHeight) {
      this._footerHeight = height;
      this.onDataLengthChanged();
    }
  }

  private _updateContent(): void {
    if (!this._viewport || this._dataLength === 0 || this._rowHeight === 0) {
      return;
    }

    if (!this._virtualScrollEnabled) {
      const start = 0;
      const end = this._dataLength;
      const currentRange = this._viewport.getRenderedRange();

      if (currentRange.start !== start || currentRange.end !== end) {
        this._viewport!.setRenderedRange({ start, end });

        this.ngZone.runOutsideAngular(() => {
          requestAnimationFrame(() => {
            if (this._viewport) {
              this._viewport.setRenderedContentOffset(0);
            }
          });
        });
      }
      return;
    }

    const rawOffset = this._viewport.measureScrollOffset() - this._headerHeight;
    const offset = Math.max(0, rawOffset);
    const viewportSize = this._viewport.getViewportSize();

    const amount = Math.ceil(viewportSize / this._rowHeight);
    const skip = Math.floor(offset / this._rowHeight);
    const index = Math.max(0, skip);
    const start = Math.max(0, index - this._bufferSize);
    const end = Math.min(this._dataLength, index + amount + this._bufferSize);

    const currentRange = this._viewport.getRenderedRange();

    if (currentRange.start !== start || currentRange.end !== end) {
      this._viewport!.setRenderedRange({ start, end });

      this.ngZone.runOutsideAngular(() => {
        requestAnimationFrame(() => {
          if (this._viewport) {
            this._viewport.setRenderedContentOffset(this._rowHeight * start);
          }
        });
      });
    }

    this._indexChange.next(index);
  }
}
